<i18n src="./resources/locales.json"></i18n>

<template>
  <v-dialog v-model="openDialog" persistent max-width="600px">
    <v-card>
      <v-card-title class="contact-card-title">
        {{ dialogTitle }}
      </v-card-title>
      <v-card-text class="contact-card-container">
        <v-container class="contact-container">
          <v-form ref="form" v-model="valid">
            <v-row>
              <v-col cols="12" md="12">
                <v-text-field
                  class="contact-input"
                  :label="$t('contactDialog.firstName')"
                  v-model="contact.firstName"
                  :rules="[rules.required, rules.maxLength]"
                  required
                  counter
                  maxlength="45"
                  ref="firstNameField"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="12">
                <v-text-field
                  class="contact-input"
                  :label="$t('contactDialog.lastName')"
                  v-model="contact.lastName"
                  counter
                  maxlength="45"
                  required
                  :rules="[rules.required, rules.maxLength]"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  class="contact-input"
                  :label="$t('contactDialog.company')"
                  v-model="contact.company"
                  counter
                  maxlength="45"
                  :rules="[rules.maxLength]"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="12">
                <v-text-field
                  class="contact-input"
                  :label="$t('contactDialog.phoneNumber')"
                  v-model="contact.number"
                  required
                  :disabled="!!contact.id && isValidPhoneNumber(contact.number) === true"
                  :rules="[rules.required, isValidPhoneNumber, isUnique]"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="12">
                <v-select
                  class="contact-input"
                  :items="contactTypes"
                  item-text="textType"
                  item-value="type"
                  v-model="contact.type"
                  :label="$t('contactDialog.type')"
                ></v-select>
              </v-col>
            </v-row>
          </v-form>
        </v-container>
      </v-card-text>
      <v-card-actions class="contact-actions">
        <v-spacer></v-spacer>
        <v-btn @click="close">
          {{ $t('contactDialog.cancel') }}
        </v-btn>
        <v-btn
          :class="{ 'sms-primary-button': valid }"
          @click="upsertContact"
          :disabled="!valid"
        >
          {{ isEditMode ? $t('contactDialog.save') : $t('contactDialog.add') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { parsePhoneNumber } from 'libphonenumber-js';
import ToastMixin from '@/mixins/ToastMixin';
import cloneDeep from 'lodash/cloneDeep';
import { CONTACT_TYPE_LIST, CONTACT_TYPE } from '@/constants/contact';
import Contact from '@/classes/v2/Contact';

export default {
  name: 'SmsContactDialog',

  mixins: [ToastMixin],

  computed: {
    ...mapGetters('user', ['userLinkId']),
    ...mapGetters('contactsV2', ['allContacts']),

    isEditMode() {
      return !!this.contact.id;
    },

    contactTypes() {
      const contactTypeList = CONTACT_TYPE_LIST.map(
        type => {
          return { type: type, textType: this.$t(`contactDialog.numberTypes.${type.toLowerCase()}`) };
        });
      return contactTypeList;
    },

    dialogTitle() {
      return this.isEditMode
        ? this.$t('contactDialog.editTitle', { name: this.contact.displayName })
        : this.$t('contactDialog.addTitle');
    }
  },

  data() {
    return {
      openDialog: false,
      valid: false,

      contact: () => {},

      rules: {
        required: value => !!value || this.$t('contactDialog.requiredMessage'),
        maxLength: v => v ? v.length <= 45 || this.$t('contactDialog.maxLengthMessage') : true,
      }
    };
  },

  created() {
    this.$root.$on('open-contact-dialog', this.open);
  },

  beforeDestroy() {
    this.$root.$off('open-contact-dialog');
  },

  methods: {
    ...mapActions('contactsV2', ['addUserContact', 'editUserContact']),

    upsertContact() {
      this.isEditMode ? this.editContact() : this.addContact();
    },

    addContact() {
      this.addUserContact(cloneDeep(this.contact))
        .then(storedContact => {
          const contact = new Contact(storedContact);
          this.showSuccess(this.$t('notifications.addContact', { name: contact.displayName }));
          this.$root.$emit('open-contact', contact);
          this.close();
        });
    },

    editContact() {
      const updatedContact = cloneDeep(this.contact.lean);
      this.editUserContact(updatedContact)
        .then(storedContact => {
          const contact = new Contact(storedContact);
          this.showSuccess(this.$t('notifications.updateContact', { name: contact.displayName }));
          this.close();
        });
    },

    close() {
      this.$refs.firstNameField.blur();
      this.openDialog = false;
      this.$refs.form.reset();
    },

    open(contact) {
      this.openDialog = true;
      this.contact = contact && contact.id ? cloneDeep(contact) : { number: contact ? contact.number : '' };
      this.contact.type = this.contact.type || CONTACT_TYPE.mobile;
      setTimeout(() => {
        this.$refs.firstNameField.focus();
      }, 250);
    },

    isUnique(phoneNumber) {
      if (this.isEditMode) return true;

      const contact = this.allContacts.find(contact => contact.number === phoneNumber);
      if (contact) {
        return this.$t('contactDialog.duplicatedNumber', { name: contact.displayName });
      } else {
        return true;
      }
    },

    isValidPhoneNumber(value = '') {
      try {
        const phoneNumber = parsePhoneNumber(value, 'US');

        if (!phoneNumber.isValid() || phoneNumber.country !== 'US') {
          return this.$t('contactDialog.invalidNumber');
        }

        return true;
      } catch {
        return this.$t('contactDialog.invalidNumber');
      }
    }
  }
};
</script>

<style>
.contact-container {
  padding-left: 0;
  padding-right: 0;
  padding-bottom: 0;
}
.contact-container .col-md-12.col-12 {
  padding: 6px 12px;
}
.contact-actions {
  padding: 8px 24px 16px 24px;
}
.contact-input {
  margin-top: 0;
}
.contact-input .v-text-field__slot {
  background-color: var(--v-input-bg2-base);
  border: 1px solid var(--v-input-border-base);
  padding-left: 10px !important;
}
.contact-input .v-text-field__slot label {
  padding-left: 10px !important;
}
.contact-input .v-select__slot {
  background-color: var(--v-input-bg2-base);
  border: 1px solid var(--v-input-border-base);
}
.contact-input .v-select__slot label {
  padding-left: 10px !important;
}
.contact-input .v-select__slot .v-select__selections {
  padding-left: 10px !important;
}
.contact-card-container {
  padding: 0 24px;
}
.contact-input .v-messages__wrapper {
  padding-left: 10px !important;
}
.contact-card-container .v-text-field .v-label--active {
  transform: translateY(-24px) scale(0.75);
}
.v-text-field > .v-input__control > .v-input__slot:before {
  border-color: transparent;
}
</style>
